import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/NavBar.css';
import { Tooltip, Box, ButtonBase, IconButton, Avatar, Typography, Divider, Menu, MenuItem, ListItemButton, ListItemIcon, ListItemText, Popover } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutButton from './LogoutButton';
import { useThemeMode } from '../../theme';
import { type NavTab, CHAT_TABS } from './navTabs';
import { useTenantInfo } from '../../contexts/TenantInfoContext';

interface NavBarProps {
  logedInUserMail: string;
  logedInUserName?: string;
  currentUser?: { name?: string; email?: string } | null;
  /** Tab set to render. Defaults to CHAT_TABS when omitted. */
  tabs?: NavTab[];
  /**
   * Track 1 (ChatScreen): pass this to get in-page tab selection.
   * Track 2 (Apps): omit this — tabs route via navigate() instead.
   */
  changeSelectedNavBar?: (index: number) => void;
  /** Index of the in-page selected tab (Track 1 only). */
  selectedNavBar?: number;
  /**
   * Drives the active-pill highlight by tab key.
   * When provided it takes precedence over selectedNavBar.
   * Track 2 passes "agents"; Track 1 omits this.
   */
  activeTabKey?: string;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] ?? '?').toUpperCase();
};

const nameFromEmail = (email: string): string => {
  const local = email.split('@')[0] ?? '';
  return local
    .split('.')
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
};

const NavBar = ({
  logedInUserMail,
  logedInUserName,
  currentUser,
  tabs = CHAT_TABS,
  changeSelectedNavBar,
  selectedNavBar = 0,
  activeTabKey,
}: NavBarProps) => {
  const navigate = useNavigate();
  const { tenantInfo } = useTenantInfo();

  const visibleTabs = tabs.filter((tab) => {
    if (tab.key === 'agents' && !(tenantInfo?.showAppsButton ?? true)) return false;
    if (tab.key === 'chat'   && !(tenantInfo?.showChatButton ?? true)) return false;
    return true;
  });
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const isDropdownVisible = Boolean(accountAnchor);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<HTMLElement | null>(null);
  const { mode, setMode } = useThemeMode();

  const email = currentUser?.email || logedInUserMail || '';
  const displayName = currentUser?.name || logedInUserName || (email ? nameFromEmail(email) : '');
  const initials = displayName ? getInitials(displayName) : '?';

  const isTabActive = (tab: NavTab): boolean => {
    if (activeTabKey !== undefined) return tab.key === activeTabKey;
    return selectedNavBar === tab.index;
  };

  const handleTabClick = (tab: NavTab) => {
    if (!tab.enabled) return;

    if (changeSelectedNavBar) {
      // Track 1: in-page selection; Agents & Apps navigates to /apps
      if (tab.key === 'agents') { navigate('/apps'); return; }
      changeSelectedNavBar(tab.index);
    } else {
      // Track 2: all tabs route; active tab is a no-op
      if (tab.key === activeTabKey) return;
      if (tab.key === 'chat') { navigate('/chat'); return; }
      if (tab.key === 'agents') { navigate('/apps'); return; }
    }
  };

  return (
    // Labelled because the app has a second nav (the conversation list), and
    // unlabelled sibling landmarks are indistinguishable in a rotor.
    <nav className="navBarContainer" aria-label="Primary">
      <div className="topNav">
        <div className="navTopSpacer" />
        <div className="iconContainer">
          {visibleTabs.map((tab) => {
            const isSelected = isTabActive(tab);
            const tooltipTitle = tab.enabled ? tab.label : 'Coming soon';
            return (
              <Tooltip key={tab.key} title={tooltipTitle} placement="right" arrow>
                {/* aria-disabled rather than disabled: a disabled button is not
                    focusable, so keyboard users could never reach the tab to
                    hear its "Coming soon" tooltip. handleTabClick no-ops.

                    aria-label is set explicitly because MUI's Tooltip otherwise
                    applies its title as the child's aria-label, which REPLACES
                    the name rather than adding to it: a tab reading "Analytics"
                    on screen announced as only "Coming soon". That drops the
                    visible label out of the accessible name entirely — a WCAG
                    2.5.3 (Label in Name) failure, and voice control saying
                    "click Analytics" would match nothing. */}
                <ButtonBase
                  className={`nav-item${isSelected ? ' active' : ''}${!tab.enabled ? ' nav-item--disabled' : ''}`}
                  onClick={() => handleTabClick(tab)}
                  aria-disabled={!tab.enabled}
                  aria-label={tab.enabled ? tab.label : `${tab.label}, coming soon`}
                  aria-current={isSelected ? 'page' : undefined}
                >
                  {/* spans, not divs: these now live inside a <button>, which
                      only permits phrasing content. CSS sets display: flex. */}
                  <span className="nav-item-pill">
                    <span className="nav-item-icon">{tab.icon}</span>
                    <span className="nav-item-label">{tab.label}</span>
                  </span>
                </ButtonBase>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <div className="helpContainer">
        <Tooltip title="Account" placement="right" arrow>
          <IconButton
            id="navbar-account-button"
            aria-label="Account"
            aria-haspopup="menu"
            aria-expanded={isDropdownVisible}
            onClick={(e) => setAccountAnchor(accountAnchor ? null : e.currentTarget)}
            sx={{
              color: 'white',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <AccountCircleIcon sx={{ fontSize: '2.2rem' }} />
          </IconButton>
        </Tooltip>

        {/* MUI Popover supplies Escape-to-close, outside-click dismissal and
            focus restoration to the trigger — all missing from the previous
            hand-rolled dropdown. */}
        <Popover
          open={isDropdownVisible}
          anchorEl={accountAnchor}
          onClose={() => setAccountAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{
            paper: {
              'aria-labelledby': 'navbar-account-button',
              sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)', ml: 1 },
            },
          }}
        >
            <Box sx={{ width: 240 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2.5, pb: 2, px: 2 }}>
                <Avatar sx={{ width: 48, height: 48, bgcolor: '#a8c4f0', color: '#1a56c4', fontWeight: 700, fontSize: '1.1rem', mb: 1.25 }}>
                  {initials}
                </Avatar>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1f2e', fontFamily: "var(--app-font-family, 'Proxima Nova')", lineHeight: 1.3 }}>
                  {displayName}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: "var(--app-font-family, 'Proxima Nova')", mt: 0.25 }}>
                  {email}
                </Typography>
              </Box>
              <Divider />
              {changeSelectedNavBar && (
                <Box sx={{ py: 0.5 }}>
                  <ListItemButton
                    onClick={(e) => setThemeMenuAnchor(e.currentTarget)}
                    sx={{ px: 2, py: 1, gap: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      <DarkModeOutlinedIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Theme"
                      slotProps={{ primary: { fontSize: '0.9rem', fontFamily: "var(--app-font-family, 'Proxima Nova')", color: '#1a1f2e' } }}
                    />
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0857c3', fontFamily: "var(--app-font-family, 'Proxima Nova')", textTransform: 'capitalize', mr: 0.25 }}>
                      {mode}
                    </Typography>
                    <KeyboardArrowDownIcon fontSize="small" sx={{ color: '#0857c3' }} />
                  </ListItemButton>
                  <Menu
                    anchorEl={themeMenuAnchor}
                    open={Boolean(themeMenuAnchor)}
                    onClose={() => setThemeMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{ paper: { sx: { borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 120 } } }}
                  >
                    <MenuItem
                      selected={mode === 'light'}
                      onClick={() => { setMode('light'); setThemeMenuAnchor(null); }}
                      sx={{ fontSize: '0.9rem', fontFamily: "var(--app-font-family, 'Proxima Nova')" }}
                    >
                      Light
                    </MenuItem>
                    <MenuItem
                      selected={mode === 'dark'}
                      onClick={() => { setMode('dark'); setThemeMenuAnchor(null); }}
                      sx={{ fontSize: '0.9rem', fontFamily: "var(--app-font-family, 'Proxima Nova')" }}
                    >
                      Dark
                    </MenuItem>
                  </Menu>
                </Box>
              )}
              <Divider />
              <Box sx={{ py: 0.5 }}>
                <LogoutButton variant="text" fullWidth showIcon />
              </Box>
            </Box>
        </Popover>
      </div>
    </nav>
  );
};

export default NavBar;
